import { useEffect, useState, useCallback } from "react";
import { Loader2, FileArchive, Download, ShieldCheck, FolderArchive, FileSpreadsheet, ScrollText, CalendarClock, Upload, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable } from "@/components/common/DataTable";
import { TooltipButton } from "@/components/common/Tooltips";
import { api, apiError, API } from "@/lib/api";
import { fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// PLD Bloque 2 — Paquete de auditoría CNBV (43ª-II). Expediente del periodo en Excel multi-hoja + ZIP cifrado.
// Feb-2026 · Auditoría FELVORA: cuando se pasa `felvoraCtx={entityId, ejercicio, razonSocial}` el
// componente opera contra los endpoints `/auditoria-felvora/*` (super_admin como auditor externo,
// con la autorización otorgada por la entidad). Sin ese prop, comportamiento legacy intacto.
export default function PaqueteAuditoria({ felvoraCtx = null }) {
  const { user, hasArea } = useAuth();
  const modoFelvora = !!felvoraCtx;
  const puedeVer = modoFelvora ? user?.role === "super_admin" : (hasArea && hasArea("cumplimiento"));
  const puedeOperar = modoFelvora
    ? user?.role === "super_admin"
    : ["oficial_cumplimiento", "admin_entidad"].includes(user?.role);

  const anioActual = new Date().getFullYear();
  const anios = modoFelvora
    ? [String(felvoraCtx.ejercicio)]
    : [anioActual, anioActual - 1, anioActual - 2].map(String);

  const [anio, setAnio] = useState(modoFelvora ? String(felvoraCtx.ejercicio) : String(anioActual));
  const [paquetes, setPaquetes] = useState([]);
  const [generando, setGenerando] = useState(false);
  const [descargando, setDescargando] = useState(null);
  const [loading, setLoading] = useState(true);

  // Endpoints dinámicos según el modo (default vs auditor FELVORA con entity_id explícito).
  const EP = modoFelvora ? {
    listar: `/auditoria-felvora/${felvoraCtx.entityId}/paquete`,
    generar: `/auditoria-felvora/${felvoraCtx.entityId}/paquete/generar`,
    descargarPrefix: `/auditoria-felvora/paquete`,
    bodyGenerar: (a) => ({ ejercicio: Number(a) }),
    resGenerarLabel: (r) => r.paquete,
  } : {
    listar: `/pld/auditoria`,
    generar: `/pld/auditoria/generar`,
    descargarPrefix: `/pld/auditoria`,
    bodyGenerar: (a) => ({ anio: a }),
    resGenerarLabel: (r) => r.paquete,
  };

  const load = useCallback(async () => {
    if (!puedeVer) { setLoading(false); return; }
    try {
      const { data } = await api.get(EP.listar);
      setPaquetes(data.paquetes || []);
    } catch (e) { /* área restringida */ } finally { setLoading(false); }
  }, [puedeVer, EP.listar]);
  useEffect(() => { load(); }, [load]);

  const generar = async () => {
    setGenerando(true);
    try {
      const { data } = await api.post(EP.generar, EP.bodyGenerar(anio));
      const pq = EP.resGenerarLabel(data) || {};
      const c = pq.conteos || {};
      toast.success(`Expediente ${anio} generado: ${c.alertas ?? 0} alerta(s), ${c.casos ?? 0} caso(s), ${c.operaciones_relevantes ?? 0} relevante(s), ${c.reportes ?? 0} reporte(s), ${c.denuncias ?? 0} denuncia(s) (agregado).`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setGenerando(false); }
  };

  const descargar = async (paq) => {
    setDescargando(paq.id);
    try {
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}${EP.descargarPrefix}/${paq.id}/descargar`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        let msg = "No se pudo descargar el paquete.";
        try { msg = (await res.json()).detail || msg; } catch { /* noop */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Expediente_PLD_${paq.periodo}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Paquete de auditoría descargado.");
    } catch (e) { toast.error(e.message); } finally { setDescargando(null); }
  };

  if (loading) return null;
  if (!puedeVer) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="auditoria-restringido">
        Tu perfil no tiene acceso al expediente PLD (área de cumplimiento restringida).
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="paquete-auditoria">
      {/* Encabezado normativo */}
      <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4 text-xs text-slate-600">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p>
          <strong className="text-slate-800">Paquete de auditoría CNBV (43ª-II).</strong> Genera, con un clic, el expediente PLD del periodo
          en un archivo Excel multi-hoja (alertas, casos y dictámenes, operaciones relevantes, perfil transaccional, reportes UIF, plazos/calendario y EBR institucional)
          empaquetado en un ZIP, almacenado cifrado en reposo, junto con los TXT de reportes y sus evidencias. Las <strong>denuncias se incluyen solo como métrica agregada</strong> (sin detalle ni identidad del denunciante, 43ª-VIII).
          Conservación {paquetes[0]?.retencion_anios ? `${paquetes[0].retencion_anios} años` : "10 años"} (51ª).
        </p>
      </div>

      {/* Generador */}
      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="auditoria-generador">
        <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
          <FileArchive className="h-4 w-4 text-primary" /> Generar expediente del periodo
        </h4>
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end">
          <div className="w-full sm:w-44">
            <label className="mb-1 block text-xs text-slate-500">Periodo (año)</label>
            <Select value={anio} onValueChange={setAnio}>
              <SelectTrigger data-testid="auditoria-anio"><SelectValue /></SelectTrigger>
              <SelectContent>{anios.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {puedeOperar ? (
            <TooltipButton tooltip="Descargar el expediente PLD del periodo para el auditor externo"
              data-testid="auditoria-generar-btn" disabled={generando} onClick={generar} className="bg-accent hover:bg-forest-700">
              {generando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FolderArchive className="mr-2 h-4 w-4" />}
              Generar expediente del periodo
            </TooltipButton>
          ) : (
            <p className="text-xs text-slate-400">Solo el Oficial de Cumplimiento o el administrador pueden generar el expediente.</p>
          )}
        </div>
      </div>

      {/* Histórico */}
      <div>
        <h5 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          <FileSpreadsheet className="h-3.5 w-3.5" /> Paquetes generados
        </h5>
        <DataTable testid="table-auditoria" rows={paquetes}
          columns={[
            { key: "periodo", label: "Periodo" },
            { key: "fecha_generacion", label: "Generado", render: (r) => fdate(r.fecha_generacion) },
            { key: "generado_por", label: "Responsable" },
            { key: "conteos", label: "Contenido", render: (r) => {
              const c = r.conteos || {};
              return <span className="text-xs text-slate-600">{c.alertas ?? 0} alertas · {c.casos ?? 0} casos · {c.operaciones_relevantes ?? 0} relevantes · {c.reportes ?? 0} reportes · {c.denuncias ?? 0} denuncias (agregado)</span>;
            } },
            { key: "sello_sha256", label: "Sello SHA-256", render: (r) => <Badge variant="outline" className="font-mono text-[10px]" title={r.sello_sha256}>{(r.sello_sha256 || "").slice(0, 12)}…</Badge> },
            { key: "acciones", label: "", render: (r) => (
              <TooltipButton tooltip="Descargar el paquete ZIP con cifrado y sello SHA-256"
                data-testid={`auditoria-descargar-${r.id}`} size="sm" variant="outline" disabled={descargando === r.id} onClick={() => descargar(r)}>
                {descargando === r.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Descargar ZIP
              </TooltipButton>
            ) },
          ]}
          emptyLabel="Aún no se ha generado ningún paquete de auditoría." />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          BLOQUE NUEVO — Informe de Auditoría PLD/FT (Disposición 52ª)
          El bloque anterior (Paquete de evidencia CNBV 43ª-II) NO se modifica.
          ═══════════════════════════════════════════════════════════════════ */}
      <InformeAuditoriaBloque puedeVer={puedeVer} puedeOperar={puedeOperar} felvoraCtx={felvoraCtx} />
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Componente: Informe de Auditoría PLD/FT (.docx) — 57 puntos, evaluación en
// blanco, seguimiento post-presentación, fecha límite 60 días con semáforo.
// ───────────────────────────────────────────────────────────────────────────
function InformeAuditoriaBloque({ puedeVer, puedeOperar, felvoraCtx = null }) {
  const modoFelvora = !!felvoraCtx;
  const anioActual = new Date().getFullYear();
  // El informe se hace del ejercicio anterior (cierre 31-dic). Se ofrecen 3.
  const ejerciciosDefault = modoFelvora
    ? [String(felvoraCtx.ejercicio)]
    : [anioActual - 1, anioActual, anioActual - 2].map(String);
  const [ejercicio, setEjercicio] = useState(modoFelvora ? String(felvoraCtx.ejercicio) : String(anioActual - 1));
  const [informes, setInformes] = useState([]);
  const [limite, setLimite] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [descargando, setDescargando] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seguimientoOpen, setSeguimientoOpen] = useState(false);
  const [seguimientoInf, setSeguimientoInf] = useState(null);
  const [seguimientoForm, setSeguimientoForm] = useState({});
  const [guardandoSeg, setGuardandoSeg] = useState(false);

  // Endpoints dinámicos según el modo (default vs auditor FELVORA con entity_id explícito).
  const IEP = modoFelvora ? {
    listar: `/auditoria-felvora/${felvoraCtx.entityId}/informe`,
    fechaLimite: `/auditoria-felvora/${felvoraCtx.entityId}/fecha-limite`,
    generar: `/auditoria-felvora/${felvoraCtx.entityId}/informe/generar`,
    descargarPrefix: `/auditoria-felvora/informe`,
    seguimientoPrefix: `/auditoria-felvora/informe`,
  } : {
    listar: `/pld/informe-auditoria`,
    fechaLimite: `/pld/informe-auditoria/fecha-limite`,
    generar: `/pld/informe-auditoria/generar`,
    descargarPrefix: `/pld/informe-auditoria`,
    seguimientoPrefix: `/pld/informe-auditoria`,
  };

  const cargar = useCallback(async () => {
    if (!puedeVer) { setLoading(false); return; }
    try {
      const [r1, r2] = await Promise.all([
        api.get(IEP.listar),
        api.get(`${IEP.fechaLimite}?anio=${ejercicio}`),
      ]);
      setInformes(r1.data || []);
      setLimite(r2.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [puedeVer, ejercicio, IEP.listar, IEP.fechaLimite]);
  useEffect(() => { cargar(); }, [cargar]);

  const generar = async () => {
    setGenerando(true);
    try {
      const { data } = await api.post(IEP.generar, { ejercicio: Number(ejercicio) });
      toast.success(`Informe ${data.ejercicio} generado. Sello ${(data.sello_sha256 || "").slice(0, 12)}…`);
      await cargar();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerando(false); }
  };

  const descargar = async (inf) => {
    setDescargando(inf.id);
    try {
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}${IEP.descargarPrefix}/${inf.id}/descargar`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("No se pudo descargar el informe.");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = inf.filename || `informe_auditoria_pld_${inf.ejercicio}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
    finally { setDescargando(null); }
  };

  const abrirSeguimiento = (inf) => {
    setSeguimientoInf(inf);
    setSeguimientoForm(inf.seguimiento || {});
    setSeguimientoOpen(true);
  };
  const guardarSeguimiento = async () => {
    if (!seguimientoInf) return;
    setGuardandoSeg(true);
    try {
      await api.put(`${IEP.seguimientoPrefix}/${seguimientoInf.id}/seguimiento`, seguimientoForm);
      toast.success("Seguimiento actualizado.");
      setSeguimientoOpen(false); setSeguimientoInf(null); setSeguimientoForm({});
      await cargar();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGuardandoSeg(false); }
  };

  if (!puedeVer) return null;
  const semColor = {
    verde: "bg-emerald-100 text-emerald-700 border-emerald-300",
    amarillo: "bg-amber-100 text-amber-800 border-amber-300",
    naranja: "bg-orange-100 text-orange-800 border-orange-300",
    rojo: "bg-red-100 text-red-700 border-red-300",
    gris: "bg-slate-100 text-slate-600 border-slate-300",
  };
  return (
    <div className="mt-6 space-y-4" data-testid="informe-auditoria-bloque">
      <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4 text-xs text-slate-600">
        <ScrollText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p>
          <strong className="text-slate-800">Informe de Auditoría PLD/FT (Disposición 52ª).</strong>{" "}
          Genera el borrador .docx precargado con la información operativa del ejercicio (57 puntos evaluables,
          apartados I–VI, cuestionario inicial y programa de trabajo). La columna <strong>Evaluación siempre sale en blanco</strong>:
          el sistema precarga la evidencia, la calificación es potestad exclusiva del auditor.
          Puntos sin evidencia se marcan literalmente como &ldquo;No se localizó evidencia en el periodo auditado&rdquo;.
          Retención 5 años (52ª A-XI).
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="informe-generador">
        <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
          <ScrollText className="h-4 w-4 text-primary" /> Generar informe .docx del ejercicio
        </h4>
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end">
          <div className="w-full sm:w-44">
            <label className="mb-1 block text-xs text-slate-500">Ejercicio auditado</label>
            <Select value={ejercicio} onValueChange={setEjercicio}>
              <SelectTrigger data-testid="informe-ejercicio"><SelectValue /></SelectTrigger>
              <SelectContent>{ejerciciosDefault.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {limite && (
            <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700" data-testid="informe-semaforo">
              <CalendarClock className="h-3.5 w-3.5 text-slate-500" />
              Fecha límite de presentación: <strong>{limite.fecha_limite}</strong>
              <Badge className={"border " + (semColor[limite.color] || "bg-slate-100")} title={limite.motivo || undefined}>
                {limite.estado}{limite.aplica !== false && limite.dias_restantes != null ? ` · ${limite.dias_restantes >= 0 ? `${limite.dias_restantes} días` : `${Math.abs(limite.dias_restantes)} días vencido`}` : ""}
              </Badge>
            </div>
          )}
          {puedeOperar ? (
            <TooltipButton tooltip="Genera el borrador .docx del informe con evidencia precargada"
              data-testid="informe-generar-btn" disabled={generando} onClick={generar} className="bg-accent hover:bg-forest-700">
              {generando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScrollText className="mr-2 h-4 w-4" />}
              Generar Informe de Auditoría (.docx)
            </TooltipButton>
          ) : (
            <p className="text-xs text-slate-400">Solo el Oficial de Cumplimiento o el administrador pueden generar el informe.</p>
          )}
        </div>
      </div>

      <div>
        <h5 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          <FileCheck2 className="h-3.5 w-3.5" /> Histórico de informes
        </h5>
        <DataTable testid="table-informes-auditoria" rows={informes} loading={loading}
          columns={[
            { key: "ejercicio", label: "Ejercicio" },
            { key: "fecha_generacion", label: "Generado", render: (r) => fdate(r.fecha_generacion) },
            { key: "generado_por", label: "Responsable" },
            { key: "sello_sha256", label: "Sello SHA-256",
              render: (r) => <Badge variant="outline" className="font-mono text-[10px]" title={r.sello_sha256}>{(r.sello_sha256 || "").slice(0, 12)}…</Badge> },
            { key: "seguimiento", label: "Presentado",
              render: (r) => {
                const s = r.seguimiento || {};
                if (s.fecha_envio_siti) return <Badge className="bg-emerald-100 text-emerald-700">Sí · {s.fecha_envio_siti}</Badge>;
                return <Badge variant="outline" className="border-slate-300 text-slate-500">Pendiente</Badge>;
              } },
            { key: "acciones", label: "", render: (r) => (
              <div className="flex gap-1.5">
                <TooltipButton tooltip="Descargar .docx"
                  data-testid={`informe-descargar-${r.id}`} size="sm" variant="outline"
                  disabled={descargando === r.id} onClick={() => descargar(r)}>
                  {descargando === r.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
                  .docx
                </TooltipButton>
                {puedeOperar && (
                  <TooltipButton tooltip="Capturar datos post-presentación (fechas, folio SITI, auditor)"
                    data-testid={`informe-seguimiento-${r.id}`} size="sm" variant="ghost"
                    onClick={() => abrirSeguimiento(r)}>
                    <Upload className="mr-1 h-3.5 w-3.5" /> Seguimiento
                  </TooltipButton>
                )}
              </div>
            ) },
          ]}
          emptyLabel="Aún no se ha generado ningún informe de auditoría." />
      </div>

      {/* Modal seguimiento */}
      <Dialog open={seguimientoOpen} onOpenChange={setSeguimientoOpen}>
        <DialogContent className="max-w-xl" data-testid="dialog-seguimiento-informe">
          <DialogHeader><DialogTitle>Seguimiento post-presentación · ejercicio {seguimientoInf?.ejercicio}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            Captura los datos requeridos por la CNBV después de que el informe fue firmado y enviado al SITI PLD/FT.
            Estos datos se preservan para la revisión del año siguiente.
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div><Label>Fecha en que lo conoció el Director General</Label>
              <Input type="date" data-testid="seg-fecha-dg"
                value={seguimientoForm.fecha_conocimiento_dg || ""}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, fecha_conocimiento_dg: e.target.value })} /></div>
            <div><Label>Fecha en que lo conoció el Comité / OC</Label>
              <Input type="date" data-testid="seg-fecha-comite"
                value={seguimientoForm.fecha_conocimiento_comite_oc || ""}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, fecha_conocimiento_comite_oc: e.target.value })} /></div>
            <div><Label>Fecha de envío al SITI PLD/FT</Label>
              <Input type="date" data-testid="seg-fecha-siti"
                value={seguimientoForm.fecha_envio_siti || ""}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, fecha_envio_siti: e.target.value })} /></div>
            <div><Label>Folio del acuse SITI</Label>
              <Input data-testid="seg-folio-acuse"
                value={seguimientoForm.folio_acuse_siti || ""}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, folio_acuse_siti: e.target.value })} /></div>
            <div><Label>Nombre del auditor</Label>
              <Input data-testid="seg-auditor-nombre"
                value={seguimientoForm.auditor_nombre || ""}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, auditor_nombre: e.target.value })} /></div>
            <div><Label>Tipo</Label>
              <Select value={seguimientoForm.auditor_tipo || ""}
                onValueChange={(v) => setSeguimientoForm({ ...seguimientoForm, auditor_tipo: v })}>
                <SelectTrigger data-testid="seg-auditor-tipo"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Interno">Interno</SelectItem>
                  <SelectItem value="Externo">Externo</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label>N° de certificado CNBV del auditor</Label>
              <Input data-testid="seg-auditor-cert"
                value={seguimientoForm.auditor_num_certificado || ""}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, auditor_num_certificado: e.target.value })} /></div>
            <div><Label>Fecha del certificado del auditor</Label>
              <Input type="date" data-testid="seg-auditor-cert-fecha"
                value={seguimientoForm.auditor_fecha_certificado || ""}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, auditor_fecha_certificado: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSeguimientoOpen(false)}>Cancelar</Button>
            <Button onClick={guardarSeguimiento} disabled={guardandoSeg}
              className="bg-forest-600 hover:bg-forest-700" data-testid="seg-guardar">
              {guardandoSeg ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileCheck2 className="mr-1 h-4 w-4" />}
              Guardar seguimiento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
